// Collection page templates; contains collection shell, overlay, and error markup.
import { t } from '../../../shared/config/translations'
import { collectionImportNoticeTemplate } from '../../components/collectionImportNoticeTemplates'
import { IS_NATIVE_APP } from '../../config/environment'

/** Native shows sync status in the pull-to-refresh control instead. */
function collectionSyncControlsTemplate(): string {
	const label = t('collection.syncNow')
	return `
                <span class="collection-sync-status" data-collection-sync-status aria-live="polite"></span>
                <button type="button" class="btn noFrame icon collection-sync-button" data-collection-sync-button aria-label="${label}" title="${label}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-refresh-cw-icon lucide-refresh-cw" aria-hidden="true"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                </button>`
}

/** Renders the main collection page shell. */
export function collectionPageTemplate(): string {
	return `
        <div id="collection-section">
            <header class="page-header">
                <h1 id="page-title">${t('collection.title')}</h1>
                <span class="collection-total-items" data-collection-item-count hidden></span>
                ${IS_NATIVE_APP ? '' : collectionSyncControlsTemplate()}
            </header>
            <div class="collection-search-row">
                <div class="collection-search-field">
                    <input
                        type="search"
                        id="collection-search-input"
                        class="collection-search-input"
                        placeholder="${t('collection.searchPlaceholder')}"
                        autocomplete="off"
                        spellcheck="false"
                    />
                    <button
                        type="button"
                        id="collection-search-button"
                        class="collection-search-button"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-search-icon lucide-search"><path d="m21 21-4.34-4.34"/><circle cx="11" cy="11" r="8"/></svg>
                    </button>
                </div>
                <button
                    type="button"
                    id="collection-filter-button"
                    class="btn outlined icon filter-icon-button collection-filter-button"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-funnel-icon lucide-funnel"><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"/></svg>
                </button>
            </div>
            <div class="collection-view-toolbar">
                <div id="filter-container" class="filter-container collection-sort-filter"></div>
                <div id="collection-display-controls"></div>
            </div>
            <section class="collection-value" hidden>
                <dl class="collection-value-scale">
                    <div class="collection-value-stat collection-value-minimum">
                        <dt>${t('collection.valueMinimum')}:</dt>
                        <dd class="loading-placeholder" data-collection-value="minimum"></dd>
                    </div>
                    <div class="collection-value-stat collection-value-median">
                        <dt>${t('collection.valueMedian')}:</dt>
                        <dd class="loading-placeholder" data-collection-value="median"></dd>
                    </div>
                    <div class="collection-value-stat collection-value-maximum">
                        <dt>${t('collection.valueMaximum')}:</dt>
                        <dd class="loading-placeholder" data-collection-value="maximum"></dd>
                    </div>
                </dl>
            </section>
            ${collectionImportNoticeTemplate('collection-import-notice', 'collection-import-notice-close')}
            <ul class="release-list"></ul>
            <nav
                class="collection-side-index"
                data-collection-side-index
                hidden
            ></nav>
            <div class="collection-next-page-loader">
                <div class="spinner"></div>
            </div>
            <div id="collection-error" class="collection-error" hidden></div>
        </div>
    `
}

/** Renders the collection filter overlay shell. */
export function collectionFiltersOverlayTemplate(): string {
	return `
        <section class="collection-filters-overlay">
            <h1 id="collection-filters-title">${t('collection.filters')}</h1>
            <div id="collection-overlay-filter-container" class="button-section filter-container filter-stack"></div>
            <div class="collection-filter-default-actions">
                <button
                    type="button"
                    class="btn secondary small"
                    data-set-filter-defaults
                    disabled
                >
                    ${t('common.setAsDefaultView')}
                </button>
                <button
                    type="button"
                    class="btn secondary small"
                    data-restore-filter-defaults
                    disabled
                >
                    ${t('common.resetToDefault')}
                </button>
            </div>
            <p class="btn count">
                <svg aria-hidden="true"><use href="#symbols-collection"></use></svg>
                <span data-collection-total-items></span>
            </p>
        </section>
    `
}

/** Renders the collection error block and retry action. */
export function collectionErrorTemplate(message: string): string {
	return `
        <p>${message}</p>
        <button id="collection-retry-btn" class="btn secondary small">${t('dialog.retry') || 'Retry'}</button>
    `
}
